import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000IdColumnDef, type U2000TableColumn } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import React, { useEffect, useRef } from "react";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useNavigate } from "react-router-dom";
import type { TFunction } from "i18next";

import type { U2000TableHandlers } from "../../u2000Core/types/U2000TableHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { doDeleteData, doExportExcel, doSearch, luHandleAppiError, type U2102Role } from "../U2102Service";
import { useU2000ExportExcelHandlers } from "../../u2000Core/hooks/handler/U2000UseExportExcelHandlers";
import { useU2000ExportExcelNotification } from "../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { NS, U2102ROLES } from "../U2102";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2102Context } from "../U2102Context";
import U2102RoleDeleteDlg from "./common/U2102RoleDeleteDlg";
import { U2102RoleListToolBar } from "./list/U2102RoleListToolBar";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from "../../u2000Core/tools/U2000QuickSearchUtils";

export function useU2102RoleListHandlerActions<T>(handlers: U2000TableHandlers<T>): U2000TableHandlers<T> {
  return handlers;
}

// Function to get columns definition
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "roleCode",
      label: t("lblRoleCode", { ns: NS }),
      widthClass: "w-[200px]",
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      type: "string",
      align: "left",
      inGridField: true,
      sortable: true,
      isCriteriaField: true,
    },
    {
      name: "roleDescription",
      label: t("lblRoleDescription", { ns: NS }),
      isDefaultColumn: true,
      type: "string",
      inGridField: true,
      sortable: true,
      isCriteriaField: true,
    },
    {
      name: "deletedAt",
      label: t("lblCndFieldDeletedAt", { ns: NS }),
      widthClass: "w-[400px]",
      type: "date",
      inGridField: true,
      align: "center",
      display: (value) => {
        return value ? <span className="font-bold text-red-600">DELETED</span> : "";
      },
      includeConditions: ["isNotNull"],
      ovverideLabelConditions: [
        {
          name: "isNotNull",
          label: t("lblCndDeletedAt", { ns: NS }),
        },
      ],
      isCriteriaField: true,
    },
    {
      name: "createdAt",
      label: t("lblFldCreatedAt", { ns: NS }),
      widthClass: "w-[150px]",
      isDefaultColumn: true,
      type: "date",
      inGridField: true,
      sortable: true,
      includeConditions: ["equals", "notequals", "greaterthan", "greaterthanorequal", "lesthan", "lesthanorequal", "interval"],
      isCriteriaField: true,
    },
  ];
}

const U2102RoleList: React.FC = () => {
  // Navigation
  const navigate = useNavigate();

  // Translation
  const { t, i18n } = useTranslation();

  // Toast
  const toast = useToast();

  // Auth context for role checking
  const { hasRole } = useU2000AuthContext();

  // Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU2102Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);

  // Initialize columns on mount or when 't' changes
  useEffect(() => {
    searchStateHandlers.setColumns(getColumnsDef(t));
    gridStateHandlers.setColumns(getColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
  }, [t]);

  // Manage Handler Actions
  const u2102RoleListHandlerActions: U2000TableHandlers<U2102Role> = useU2102RoleListHandlerActions<U2102Role>({
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (role: U2102Role) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers ? gridStateHandlers.getData().findIndex((r) => r.id === role.id) : -1;
      if (gridStateHandlers) {
        gridStateHandlers.setCurrentIdx(currentIdx);
      }
      navigate(`${role.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U2102");
      navigate("new");
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U2102");
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
    onExportExcel: async () => {
      const filter = searchStateHandlers.extractSearchParameter();
      await handleExportExcel.handleExportExcel(filter);
    },
  });

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U2102Role>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U2102Role>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? "/U2102";
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const singleRole = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom("");
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === singleRole.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, singleRole.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Export Excel Handlers for Logical Unit
  const handleExportExcel = useU2000ExportExcelHandlers({
    doExportExcelFn: async (filter, lang) => {
      return await doExportExcel(u2000AxiosAuthenticated, filter, lang);
    },
    i18nLanguage: i18n.language,
    onSuccess: (processId: string) => {
      toast.success(
        t("msgExportExcelStarted", { processId, ns: "common" }) || `Export en cours (référence: ${processId})`,
        t("successTitle", { ns: "common" })
      );
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Écouter les notifications WebSocket pour les exports Excel terminés
  useU2000ExportExcelNotification();

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U2102",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  // Gestion des Delete Record avec confirmation
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["roleCode"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Après suppression, aller au prochain enregistrement, aller vers l'avant si possible, sinon vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // s'assurer que les colonnes sont chargées avant le rendu car creer des problemes d'affichage avec les columnes par defaut si searchStateHandlers.columns est vide au rendu
  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  // Render
  return (
    <>
      <U2102RoleDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={<U2102RoleListToolBar gridStateHandlers={gridStateHandlers as any} handlerActions={u2102RoleListHandlerActions as any} />}
        handlerActions={u2102RoleListHandlerActions}
        label={t("lblRole", {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        exportExcelTitle={t("btnExportExcel", { ns: "common" })}
        isExporting={handleExportExcel.isExporting}
        hasExportRole={hasRole(U2102ROLES.EXPORT)}
      />
    </>
  );
};

export default U2102RoleList;
